import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import { getUserOffers, issueOfferWithCredit, removeOfferWithCredit, AVAILABLE_OFFERS } from '../services/api'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import { useAuth } from './auth/AuthProvider'
import UserNotesModal from './UserNotesModal'

const OffersContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const OffersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 2rem;
  flex-wrap: wrap;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const UserNotesButton = styled(Button)`
  background: #fefacd;
  color: #444;
  border: 2px solid #d4a017;
  font-weight: 600;

  &:hover:not([disabled]) {
    background: #fef9b8;
    border-color: #b8860b;
  }
`

const CurrentOffersSection = styled.div`
  margin-bottom: 3rem;
`

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const OffersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const OfferItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${theme.color.background.lightGrey};
  border-radius: 4px;
  border-left: 4px solid ${theme.color.accent.primary};
`

const OfferInfo = styled.div`
  flex: 1;
`

const OfferName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.25rem;
`

const OfferDetails = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
`

const OfferQuantity = styled.div`
  background: ${theme.color.accent.primary};
  color: ${theme.color.text.light};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 1.2rem;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`

const OfferActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not([disabled]) {
    background: #c82333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const RemoveQuantitySelect = styled.select`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid ${theme.color.border.light};
  font-size: 1.1rem;
`

const IssueOffersContainer = styled.div`
  border-top: 1px solid ${theme.color.border.light};
  padding-top: 2rem;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`

const IssueOffersSection = styled.div`
  flex: 1;
  min-width: 300px;
`

const IssueForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`

const Label = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Select = styled.select`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const NoOffersMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #007700;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

interface OffersSectionProps {
  userData: any
  onUpdate: () => void
}

const OffersSection: React.FC<OffersSectionProps> = ({ userData, onUpdate }) => {
  const { logEvent } = useAuditLog()
  const { isSuperAdmin } = useAuth()
  const [offers, setOffers] = useState<any[]>([])
  const [isLoadingOffers, setIsLoadingOffers] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<string>('')
  const [isIssuingOffer, setIsIssuingOffer] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [removingOfferName, setRemovingOfferName] = useState<string | null>(null)
  const [removeQuantities, setRemoveQuantities] = useState<Record<string, number>>({})

  // User Notes state
  const [showUserNotes, setShowUserNotes] = useState(false)

  useEffect(() => {
    if (userData?.user?.id) {
      loadUserOffers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user?.id])

  const loadUserOffers = async () => {
    if (!userData?.user?.id) return

    setIsLoadingOffers(true)
    try {
      const offersData = await getUserOffers(userData.user.id)
      console.log('Offers API response:', offersData)
      setOffers(Array.isArray(offersData) ? offersData : [])
    } catch (err) {
      console.error('Failed to load offers:', err)
      setOffers([])
    } finally {
      setIsLoadingOffers(false)
    }
  }

  const handleIssueOffer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedOffer || !userData?.user?.id) return

    setIsIssuingOffer(true)
    setMessage(null)

    try {
      // Use enhanced offer issuance that also creates Mariana Tek credits
      const marianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek;
      const result = await issueOfferWithCredit(userData.user.id, selectedOffer, marianaTekId);
      
      console.log('Enhanced offer issuance result:', result);
      
      // Log audit event
      await logEvent({
        tabName: 'Current Offers',
        action: AUDIT_ACTIONS.OFFER_ISSUED,
        details: {
          offer_name: selectedOffer,
          offer_id: AVAILABLE_OFFERS[selectedOffer as keyof typeof AVAILABLE_OFFERS],
          mariana_tek_credit_created: !!result.marianaTek,
          mariana_tek_id: marianaTekId
        },
        targetUserId: userData.user.id,
        targetUserEmail: userData.user?.email
      })
      
      setMessage({ type: 'success', text: 'Offer issued successfully' })
      setSelectedOffer('')
      await loadUserOffers() // Refresh offers

      // Refresh user data with error handling
      try {
        await onUpdate()
      } catch (updateErr) {
        console.error('Failed to refresh user data after issuing offer:', updateErr)
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to issue offer'
      })
    } finally {
      setIsIssuingOffer(false)
    }
  }

  const handleRemoveOffer = async (groupedOffer: any) => {
    if (!userData?.user?.id) return
    const qty = removeQuantities[groupedOffer.name] || 1
    if (!window.confirm(`Remove ${qty} "${groupedOffer.name}" offer${qty > 1 ? 's' : ''}?`)) return

    setRemovingOfferName(groupedOffer.name)
    setMessage(null)

    try {
      const marianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek
      const instancesToRemove = groupedOffer.instances.slice(0, qty)

      for (const instance of instancesToRemove) {
        await removeOfferWithCredit(userData.user.id, groupedOffer.name, instance.offer_instance_id, marianaTekId)
      }

      await logEvent({
        tabName: 'Current Offers',
        action: AUDIT_ACTIONS.OFFER_ISSUED,
        details: {
          action: 'removed',
          offer_name: groupedOffer.name,
          quantity_removed: qty,
          mariana_tek_id: marianaTekId
        },
        targetUserId: userData.user.id,
        targetUserEmail: userData.user?.email
      })

      setMessage({ type: 'success', text: `${qty} "${groupedOffer.name}" removed successfully` })
      setRemoveQuantities(prev => { const next = { ...prev }; delete next[groupedOffer.name]; return next })
      await loadUserOffers()

      try {
        await onUpdate()
      } catch (updateErr) {
        console.error('Failed to refresh user data after removing offer:', updateErr)
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove offer'
      })
    } finally {
      setRemovingOfferName(null)
    }
  }

  const groupOffersByName = (offers: any[]) => {
    const grouped = offers.reduce((acc, offer: any) => {
      const name = offer.name || 'Unknown Offer'
      if (!acc[name]) {
        acc[name] = {
          ...offer,
          quantity: 0,
          instances: []
        }
      }
      acc[name].quantity += 1
      acc[name].instances.push(offer)
      return acc
    }, {} as Record<string, any>)

    return Object.values(grouped)
  }

  const formatExpirationDate = (dateString: string) => {
    if (!dateString) return 'No expiration'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  if (!userData || !userData.user) {
    return null
  }

  const groupedOffers = groupOffersByName(offers)

  return (
    <OffersContainer>
      <OffersHeader>
        <Title>Current Offers</Title>
        <UserNotesButton onClick={() => setShowUserNotes(true)}>
          User Notes
        </UserNotesButton>
      </OffersHeader>

      <CurrentOffersSection>
        <SectionTitle>Active Offers</SectionTitle>
        
        {isLoadingOffers ? (
          <LoadingMessage>Loading offers...</LoadingMessage>
        ) : groupedOffers.length > 0 ? (
          <OffersList>
            {(groupedOffers as any[]).map((offer: any, index) => (
              <OfferItem key={index}>
                <OfferInfo>
                  <OfferName>{offer.name}</OfferName>
                  <OfferDetails>
                    Expires: {formatExpirationDate(offer.expiration_date)}
                  </OfferDetails>
                </OfferInfo>
                <OfferActions>
                  <OfferQuantity>{offer.quantity}</OfferQuantity>
                  {isSuperAdmin && (
                    <>
                      <RemoveQuantitySelect
                        value={removeQuantities[offer.name] || 1}
                        onChange={(e) => setRemoveQuantities(prev => ({ ...prev, [offer.name]: parseInt(e.target.value) }))}
                        disabled={removingOfferName === offer.name}
                      >
                        {Array.from({ length: offer.quantity }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </RemoveQuantitySelect>
                      <RemoveButton
                        onClick={() => handleRemoveOffer(offer)}
                        disabled={removingOfferName === offer.name}
                      >
                        {removingOfferName === offer.name ? 'Removing...' : 'Remove'}
                      </RemoveButton>
                    </>
                  )}
                </OfferActions>
              </OfferItem>
            ))}
          </OffersList>
        ) : (
          <NoOffersMessage>No active offers found</NoOffersMessage>
        )}
      </CurrentOffersSection>

      <IssueOffersContainer>
        <IssueOffersSection>
          <SectionTitle>Issue New Offer</SectionTitle>
          <IssueForm onSubmit={handleIssueOffer}>
            <FormGroup>
              <Label htmlFor="offerSelect">Select Offer</Label>
              <Select
                id="offerSelect"
                value={selectedOffer}
                onChange={(e) => setSelectedOffer(e.target.value)}
              >
                <option value="">Choose an offer...</option>
                {Object.keys(AVAILABLE_OFFERS).map(offerName => (
                  <option key={offerName} value={offerName}>
                    {offerName}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <Button
              type="submit"
              primary
              disabled={isIssuingOffer || !selectedOffer}
            >
              {isIssuingOffer ? 'Issuing...' : 'Issue Offer'}
            </Button>
          </IssueForm>

          {message && (
            message.type === 'success' ?
              <SuccessMessage>{message.text}</SuccessMessage> :
              <ErrorMessage>{message.text}</ErrorMessage>
          )}
        </IssueOffersSection>

      </IssueOffersContainer>

      <UserNotesModal
        isOpen={showUserNotes}
        onClose={() => setShowUserNotes(false)}
        userData={userData}
      />
    </OffersContainer>
  )
}

export default OffersSection